/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/require-await */
import { DefaultGameState, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
:host {
    z-index: 2;
    display: none;
}

#close {
    position: absolute;
    right: 0%;
    top: 0%;
}

.inventory {
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
}

.inventory-container {
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    color: #d8d8d8;
    padding: 20px;
    width: 1000px;
    opacity: 0.92;
    height: 611px;
    display: flex;
    flex-direction: column;
}

.image {
    width: 100%;
    height: 100%;
    cursor: pointer; /* Make the image clickable */
}

.inventory-intro {
    font-size: 30px;
    margin: 50px auto;
    text-align: center;
    width: 80%;
    border-bottom: 2px solid;
    padding-bottom: 10px;
    font-family: 'Cinzel', serif;
}

.inventory-slot {
    margin: 20px;
    border-radius: 8px;
    height: 110px;
    width: 110px;
    background-color: #333;
    border: 2px solid #555;
    transition: box-shadow 0.3s ease;
    text-align: center;
    position: relative;
    overflow: hidden;
    transition: transform 0.3s ease; /* Add a smooth transition effect */
    transform: scale(1.1); /* Scale up the image on hover */
}

.inventory-slot:hover {
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.4); /* More prominent shadow */
    transform: scale(1.1); /* Scale up the image on hover */
}

.inventory-slots {
    height: 75%;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    border: 2px solid #555;
}

.inventory-slot p {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 14px;
}

.equipped {
    outline: 3px solid gold;
    outline-offset: -3px; /* Zorgt dat de outline dicht bij het element blijft */
}
`;

export class InventoryComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        // Controleer of er een opgeslagen state is in localStorage
        const savedState: string | null = localStorage.getItem("gameState");

        if (savedState) {
            // Gebruik de opgeslagen game state
            console.log("Loaded game state from localStorage");
            this.updateGameState(JSON.parse(savedState) as GameState);
        }
        else {
            // Haal de game state op via de API als er niks in localStorage staat
            const state: GameState = await this._gameRouteService.getGameState();
            this.updateGameState(state);
        }
    }

    /**
         * Update the canvas to the provided game state
         *
         * @param state Game state to update the canvas to
         */
    private updateGameState(state: GameState): void {
        // Controleer of de game een pagina-wissel vereist
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }

        this._currentGameState = state;

        // Zorg ervoor dat het equipped item correct wordt ingeladen
        this.equippedItem = state.equippedWeapon;

        // Render de UI opnieuw om de wijzigingen te tonen
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderInventory()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        console.log(this._currentGameState);

        this.shadowRoot.append(...elements);
    }

    private equipableItems: string[] = ["Dragon-Slayer", "Rusty-Dagger", "Rusty-Sword"];
    private equippedItem: string | null = null;

    private renderInventory(): HTMLElement {
        const inventory: string[] | undefined = this._currentGameState?.inventory;
        //  Haal het opgeslagen item op
        const equippedItem: string | undefined = this._currentGameState?.equippedWeapon;

        setTimeout(() => {
            // Zoek de sluitknop en voeg een eventlistener toe
            const closeDiv: HTMLElement | null = this.shadowRoot!.getElementById("inventoryButton");
            if (closeDiv) {
                closeDiv.addEventListener("click", () => {
                    this.style.display = "none"; // Sluit het inventory-scherm
                });
            }

            // Loop door de inventory en voeg eventlisteners toe aan elk item
            if (inventory) {
                for (let i: number = 0; i < inventory.length; i++) {
                    const itemElement: HTMLElement | null = this.shadowRoot!.getElementById(`inventory-item${i}`);
                    if (itemElement) {
                        itemElement.addEventListener("click", () => {
                            this.toggleEquip(inventory[i], itemElement);
                        });

                        // Voeg de 'equipped' class toe als het item overeenkomt met het opgeslagen item
                        if (inventory[i] === equippedItem) {
                            itemElement.classList.add("equipped");
                        }
                    }
                }
            }
        });

        // Genereer de HTML voor de inventory
        return html`
            <section class="inventory">
                <div class="inventory-container">
                    <div class="inventory-intro">Inventory</div>
                    <div class="inventory-slots">
                        ${inventory?.map((item, index) => html`
                            <div class="inventory-slot" key="${index}">
                                <img 
                                    class="image ${item === equippedItem ? "equipped" : ""}" 
                                    src="/assets/img/rooms/${item}.png" 
                                    alt="${item}"
                                    id="inventory-item${index}" />
                            </div>
                        `) ?? html`
                            <p>No items in inventory</p>
                        `}
                    </div>
                </div>
            </section>
        `;
    }

    private toggleEquip(item: string, element: HTMLElement): void {
        // Controleer of het item equipable is
        if (!this.equipableItems.includes(item)) {
            return;
        }

        // Haal het vorige equipped item op en verwijder de gele border
        const previousEquipped: HTMLElement | null = this.shadowRoot!.querySelector(".equipped");
        if (previousEquipped) {
            previousEquipped.classList.remove("equipped");
        }

        // Check of het item al is equiped en toggle het
        if (this.equippedItem === item) {
            // Unequip het item
            this.equippedItem = null;
        }
        else {
            // Equip het nieuwe item
            this.equippedItem = item;
            // Voeg de border toe
            element.classList.add("equipped");
        }

        // Werk de game state bij en sla het equipped item op in localStorage
        this.updateEquipState();
    }

    private async updateEquipState(): Promise<void> {
        if (!this._currentGameState) return;

        // Maak een kopie van de huidige game state (voorkomt onbedoelde mutaties)
        const newState: GameState = { ...this._currentGameState };

        // Update de equippedWeapon
        newState.equippedWeapon = this.equippedItem ?? "";

        // Log en update de UI
        console.log("Updated game state:", newState);
        this.updateGameState(newState);

        // Sla de nieuwe game state op in localStorage
        localStorage.setItem("gameState", JSON.stringify(newState));
    }
}
